import { Icon } from "@tutor/ui";
import { useCallback, useEffect, useState, type SyntheticEvent } from "react";
import { useLocale } from "../i18n";
import * as api from "./api";
import { errorKey } from "./errors";
import { useParentT, type ParentStringKey } from "./strings";

// Parents remembered on the child's device (ADR-0048): the PIN screen that opens parent
// mode, the offer to stay signed in after an email code, and the list of remembered
// devices. A PIN session is never fresh, so lifting a pause, consent and pairing still
// ask for an email code; the server enforces that, these screens only explain it.

const PIN = /^\d{6}$/;

function PinInput(props: { label: string; value: string; onChange: (pin: string) => void }) {
  return (
    <label className="parent-label">
      {props.label}
      <input
        className="parent-input pm-pin"
        type="password"
        inputMode="numeric"
        autoComplete="off"
        maxLength={6}
        value={props.value}
        onChange={(e) => {
          props.onChange(e.target.value.replace(/\D/g, ""));
        }}
      />
    </label>
  );
}

/** Opens parent mode with a parent PIN. `onForgotten` means only an email code works now. */
export function Unlock(props: {
  device: string;
  grants: readonly api.DeviceGrant[];
  onUnlocked: () => void;
  onUseEmail: (notice?: ParentStringKey) => void;
}) {
  const { device, grants, onUnlocked, onUseEmail } = props;
  const t = useParentT();
  const [grantId, setGrantId] = useState(grants[0]?.id ?? "");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<ParentStringKey>();

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (busy || !PIN.test(pin)) return;
    setBusy(true);
    const res = await api.unlockWithPin(device, grantId, pin);
    setBusy(false);
    setPin("");
    if (res.ok) {
      onUnlocked();
      return;
    }
    const code = res.error.kind === "http" ? res.error.code : undefined;
    if (code === "locked" || code === "not_found") {
      onUseEmail("unlock.locked");
      return;
    }
    setMessage(code === "wrong_pin" ? "unlock.wrong" : errorKey(res.error));
  };

  return (
    <section className="parent-panel" aria-labelledby="parent-unlock-title">
      <h2 id="parent-unlock-title" className="parent-heading">
        {t("unlock.title")}
      </h2>
      <form className="parent-form" onSubmit={(e) => void submit(e)} noValidate>
        {grants.length > 1 ? (
          <fieldset className="pm-who">
            <legend className="parent-label">{t("unlock.who")}</legend>
            {grants.map((g) => (
              <label key={g.id} className="pm-chip" data-on={g.id === grantId ? "true" : undefined}>
                <input
                  type="radio"
                  name="parent"
                  checked={g.id === grantId}
                  onChange={() => {
                    setGrantId(g.id);
                  }}
                />
                {g.parent}
              </label>
            ))}
          </fieldset>
        ) : (
          <p className="parent-muted">{t("unlock.body", { parent: grants[0]?.parent ?? "" })}</p>
        )}
        <PinInput label={t("unlock.pinLabel")} value={pin} onChange={setPin} />
        {message === undefined ? null : (
          <p className="parent-message" role="alert">
            {t(message)}
          </p>
        )}
        <button
          className="parent-button parent-button-primary"
          type="submit"
          disabled={busy || pin.length !== 6}
        >
          {t("unlock.submit")}
        </button>
        <button
          className="parent-link"
          type="button"
          onClick={() => {
            onUseEmail();
          }}
        >
          {t("unlock.useEmail")}
        </button>
      </form>
    </section>
  );
}

/** After an email code: offer to remember this parent here with a parent PIN. */
export function Remember(props: {
  device: string;
  onDone: () => void;
  onAuthError: (error: api.ApiError) => boolean;
}) {
  const { device, onDone, onAuthError } = props;
  const t = useParentT();
  const [pin, setPin] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<ParentStringKey>();

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (busy || !PIN.test(pin)) return;
    if (pin !== again) {
      setMessage("remember.mismatch");
      return;
    }
    setBusy(true);
    const res = await api.rememberDevice(device, pin);
    setBusy(false);
    if (res.ok) {
      onDone();
      return;
    }
    if (onAuthError(res.error)) return;
    const code = res.error.kind === "http" ? res.error.code : undefined;
    setMessage(
      code === "trivial_pin"
        ? "remember.trivial"
        : code === "same_as_child_pin"
          ? "remember.childPin"
          : errorKey(res.error),
    );
  };

  return (
    <section className="parent-panel" aria-labelledby="parent-remember-title">
      <h2 id="parent-remember-title" className="parent-heading">
        {t("remember.title")}
      </h2>
      <form className="parent-form" onSubmit={(e) => void submit(e)} noValidate>
        <p className="parent-muted">{t("remember.body")}</p>
        <PinInput label={t("remember.pinLabel")} value={pin} onChange={setPin} />
        <PinInput label={t("remember.confirmLabel")} value={again} onChange={setAgain} />
        <p className="parent-muted pm-small">{t("remember.hint")}</p>
        {message === undefined ? null : (
          <p className="parent-message" role="alert">
            {t(message)}
          </p>
        )}
        <button
          className="parent-button parent-button-primary"
          type="submit"
          disabled={busy || pin.length !== 6 || again.length !== 6}
        >
          {t("remember.submit")}
        </button>
        <button className="parent-link" type="button" disabled={busy} onClick={onDone}>
          {t("remember.skip")}
        </button>
      </form>
    </section>
  );
}

/**
 * The devices where this parent stays signed in, each with « Retirer », and « Se
 * déconnecter partout », after which parent mode closes (`onSignedOut`).
 */
export function RememberedDevices(props: {
  thisDeviceId: string | undefined;
  onAuthError: (error: api.ApiError) => boolean;
  onSignedOut: () => void;
}) {
  const { thisDeviceId, onAuthError, onSignedOut } = props;
  const t = useParentT();
  const locale = useLocale();
  const [grants, setGrants] = useState<readonly api.ParentGrant[] | null>(null);
  const [message, setMessage] = useState<ParentStringKey>();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api.listGrants();
    if (res.ok) setGrants(res.value.grants);
    else if (!onAuthError(res.error)) setMessage(errorKey(res.error));
  }, [onAuthError]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (grantId: string) => {
    const res = await api.revokeGrant(grantId);
    if (!res.ok) {
      if (!onAuthError(res.error)) setMessage(errorKey(res.error));
      return;
    }
    setGrants((list) => list?.filter((g) => g.id !== grantId) ?? null);
  };

  const signOutEverywhere = async () => {
    setBusy(true);
    const res = await api.signOutEverywhere();
    setBusy(false);
    if (!res.ok) {
      if (!onAuthError(res.error)) setMessage(errorKey(res.error));
      return;
    }
    onSignedOut();
  };

  const date = new Intl.DateTimeFormat(locale, { dateStyle: "medium" });
  const when = (iso: string) => {
    const at = Date.parse(iso);
    return Number.isFinite(at) ? date.format(at) : iso;
  };

  return (
    <section className="pm-group" aria-labelledby="pm-devices-title">
      <h3 id="pm-devices-title" className="pm-group__title">
        {t("devices.title")}
      </h3>
      {message === undefined ? null : (
        <p className="parent-message" role="alert">
          {t(message)}
        </p>
      )}
      {grants === null ? null : grants.length === 0 ? (
        <p className="parent-muted">{t("devices.empty")}</p>
      ) : (
        grants.map((g) => (
          <div key={g.id} className="pm-row" data-grant={g.id}>
            <div className="pm-row__line">
              <span className="pm-row__label">
                <Icon name="lightbulb" size={16} className="pm-device__icon" />
                {g.deviceLabel}
                {g.deviceId === thisDeviceId ? (
                  <span className="pm-tag">{t("devices.this")}</span>
                ) : null}
              </span>
              <span className="pm-row__control">
                <button type="button" className="parent-button" onClick={() => void remove(g.id)}>
                  {t("devices.remove")}
                </button>
              </span>
            </div>
            <p className="parent-muted pm-small">
              {g.lastUnlockedAt === null
                ? t("devices.until", { date: when(g.expiresAt) })
                : `${t("devices.lastUsed", { date: when(g.lastUnlockedAt) })} · ${t("devices.until", { date: when(g.expiresAt) })}`}
            </p>
          </div>
        ))
      )}
      {confirming ? (
        <div className="pm-row">
          <p className="parent-muted pm-small">{t("devices.signOutAllBody")}</p>
          <div className="pm-row__line">
            <button
              type="button"
              className="parent-button"
              disabled={busy}
              onClick={() => void signOutEverywhere()}
            >
              {t("devices.signOutAllConfirm")}
            </button>
            <button
              type="button"
              className="parent-link"
              disabled={busy}
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t("devices.signOutAllCancel")}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="parent-link"
          onClick={() => {
            setConfirming(true);
          }}
        >
          {t("devices.signOutAll")}
        </button>
      )}
    </section>
  );
}
